import { C } from '@ridgewill/brand';
import { adminApi, type FeedEvent, type Overview as OverviewData, type OverviewApplication } from '../lib/api.js';
import { useAsync } from '../lib/useAsync.js';
import { count, numeric } from '../lib/format.js';
import {
  AsyncBoundary,
  Badge,
  DotPill,
  EmptyState,
  Identity,
  Num,
  Platforms,
  When,
} from '../components/ui.js';

/**
 * The dashboard.
 *
 * ── It was seven flat tiles, and a dashboard is not a list of counts ──
 *
 * Every number was a total with nothing to compare it against. "2,502 users" is
 * a fact about the database; "2,502 users, +14 this week, 31 active" is a fact
 * about the platform, and only the second one can be read at a glance to decide
 * whether anything needs attention.
 *
 * So four KPIs with a trend line each, then the two things that answer "what is
 * going on right now" — the applications and the last few events.
 *
 * ── Still counts, still no time series ───────────────────────────────
 *
 * The server counts what other surfaces already store, so this cannot disagree
 * with the screens it summarises. And there are no charts, because this is the
 * page most likely to be left open on a second monitor refreshing all afternoon
 * against the same database the sign-in path uses. A trend here is one extra
 * integer, not a query over a window.
 */
export function Overview({ navigate }: { navigate: (to: string) => void }) {
  const { state } = useAsync(() => adminApi.overview());

  return (
    <AsyncBoundary state={state}>
      {(d) => {
        /**
         * EACH SECTION ONLY IF IT ARRIVED (RidgeWill/auth#518). auth sends a
         * section only to a role that can read the screen it summarises, so a
         * Legal Admin's Overview is the Agreements figures and nothing else,
         * rather than a dashboard of account and activity data its nav hides.
         */
        const shown = overviewSections(d);
        if (!shown.any) {
          return (
            <EmptyState
              title="Nothing to summarise for your role"
              suggestion="The Overview shows figures from the screens your role can open."
            />
          );
        }
        return (
          <div style={{ display: 'grid', gap: '1rem' }} className="rw-fade">
            <div style={s.kpis}>
              {d.users ? (
                <>
                  <Kpi
                    label="Total users"
                    value={d.users.total}
                    trend={
                      d.users.newThisWeek > 0 ? `+${count(d.users.newThisWeek)} this week` : 'None this week'
                    }
                    accent={C.brand}
                  />
                  <Kpi
                    label="Active this week"
                    value={d.users.activeThisWeek}
                    /* Counts ACCOUNTS, not sign-ins — one enthusiastic user is one
                       active account. The server does that with count(DISTINCT …). */
                    trend={`${percent(d.users.activeThisWeek, d.users.total)} of accounts`}
                    accent={C.turquoise}
                  />
                </>
              ) : null}
              {d.applications ? (
                <Kpi
                  label="Applications"
                  value={d.applications.total}
                  trend={`${count(d.applications.live)} live`}
                  accent={C.violet}
                />
              ) : null}
              {d.agreements ? (
                <Kpi
                  label="Agreements"
                  value={d.agreements.active}
                  /* The one number here that is a QUEUE rather than a fact: it only
                     goes down when people accept, so a rising value means a version
                     was published that nobody is signing. */
                  trend={`${count(d.agreements.usersOwing)} users owing`}
                  accent={d.agreements.usersOwing > 0 ? C.warnDot : C.successDot}
                  alert={d.agreements.usersOwing > 0}
                />
              ) : null}
            </div>

            {d.applications ? (
              <div style={s.split}>
                <Applications rows={d.applications.rows} navigate={navigate} />
                {shown.platform || shown.activity ? (
                  <div style={{ display: 'grid', gap: '1rem', alignContent: 'start' }}>
                    {shown.platform ? (
                      <Platform users={d.users} signIns={d.signIns} credentials={d.credentials} />
                    ) : null}
                    {d.recentActivity ? <RecentActivity events={d.recentActivity} navigate={navigate} /> : null}
                  </div>
                ) : null}
              </div>
            ) : shown.platform || shown.activity ? (
              /* Without Applications, Platform and Recent activity sit SIDE BY
                 SIDE rather than stacked full width, where a two-row panel
                 stretched across the whole screen (Jon, 2026-09-16, a Support
                 Admin's Overview). They wrap to one column when narrow. */
              <div style={s.pair}>
                {shown.platform ? (
                  <Platform users={d.users} signIns={d.signIns} credentials={d.credentials} />
                ) : null}
                {d.recentActivity ? <RecentActivity events={d.recentActivity} navigate={navigate} /> : null}
              </div>
            ) : null}
          </div>
        );
      }}
    </AsyncBoundary>
  );
}

/**
 * Which parts of the Overview have anything to show, from what auth sent.
 *
 * Exported and pure because this repo has no DOM tests, and the layout rule it
 * carries is the one that goes quietly wrong: a panel rendered around nothing,
 * or a two-column split with one column missing.
 */
export function overviewSections(d: OverviewData): {
  any: boolean;
  applications: boolean;
  platform: boolean;
  activity: boolean;
} {
  const applications = d.applications !== null;
  const platform = d.users !== null || d.signIns !== null || d.credentials !== null;
  const activity = d.recentActivity !== null;
  return {
    any: applications || platform || activity || d.agreements !== null,
    applications,
    platform,
    activity,
  };
}

function Kpi({
  label,
  value,
  trend,
  accent,
  alert,
}: {
  label: string;
  value: number;
  trend: string;
  accent: string;
  alert?: boolean;
}) {
  return (
    <div style={s.kpi}>
      {/* The accent bar, not a coloured background. A tinted card competes with
          the number on it; a 3px rail identifies the card and gets out of the
          way. */}
      <span style={{ ...s.accent, background: accent }} aria-hidden />
      <span style={s.kpiLabel}>{label}</span>
      <span style={{ ...s.kpiValue, ...numeric }}>{count(value)}</span>
      <span style={{ ...s.kpiTrend, color: alert ? C.warnText : C.muted }}>{trend}</span>
    </div>
  );
}

/** Two thirds of the width, because it is the table people actually read. */
function Applications({
  rows,
  navigate,
}: {
  rows: OverviewApplication[];
  navigate: (to: string) => void;
}) {
  return (
    <section style={s.panel}>
      <header style={s.panelHead}>
        <h2 style={s.panelTitle}>Applications</h2>
        <button type="button" onClick={() => navigate('/applications')} style={s.link}>
          View all
        </button>
      </header>

      {rows.length === 0 ? (
        <EmptyState
          title="No applications registered"
          suggestion="Register one on the Applications screen to grant a security group access to it."
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                {['Application', 'Platform', 'Users', 'Status'].map((c) => (
                  <th key={c} style={{ ...s.th, ...(c === 'Users' ? s.thRight : {}) }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="rw-rowhover">
                  <td style={s.td}>
                    {/*
                     * A BUTTON that navigates, not a double-click on the row.
                     *
                     * The old portal opened this with a double-click and a
                     * `title` attribute as the only hint, which is not
                     * discoverable and fights text selection — `userSelect:
                     * none` was set to paper over it. One convention across all
                     * five lists: the name is the control, and it says so by
                     * looking like one.
                     */}
                    <button
                      type="button"
                      onClick={() => navigate(`/applications/${a.slug}`)}
                      style={s.rowLink}
                    >
                      {a.name}
                    </button>
                    {a.description ? <div style={s.sub}>{a.description}</div> : null}
                  </td>
                  <td style={s.td}>
                    <Platforms value={a.platform} />
                  </td>
                  <td style={{ ...s.td, ...s.tdRight, ...numeric }}>
                    {/* "Accounts holding a role that grants this app" — there is
                        no user-to-application join, because entitlement is per
                        role. Said in the title so the number is not misread as
                        usage. */}
                    <span title="Accounts holding a role that grants this application">
                      {count(a.users)}
                    </span>
                  </td>
                  <td style={s.td}>
                    <StatusPill status={a.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'live'
      ? 'good'
      : status === 'alpha' || status === 'beta' || status === 'in-dev'
        ? 'warn'
        : status === 'retired'
          ? 'danger'
          : 'muted';
  return <DotPill tone={tone}>{status}</DotPill>;
}

/**
 * The health of the platform itself, as distinct from the apps on it.
 *
 * This is where the old portal's "System health" card sat, and it showed the
 * Identity API and database — both of which are answered by this screen having
 * rendered at all. So it holds the numbers that actually indicate something is
 * wrong instead: failed sign-ins, bans, and how many credentials exist.
 */
function Platform({
  users,
  signIns,
  credentials,
}: {
  users: OverviewData['users'];
  signIns: OverviewData['signIns'];
  credentials: OverviewData['credentials'];
}) {
  return (
    <section style={s.panel}>
      <header style={s.panelHead}>
        <h2 style={s.panelTitle}>Platform</h2>
        <DotPill tone="good">admin API reachable</DotPill>
      </header>
      <dl style={s.rows}>
        {signIns ? (
          <Row
            label="Sign-ins (24h)"
            value={<Num value={signIns.last24h} />}
            note={
              signIns.failedLast24h > 0 ? (
                /* Counts EVENTS, not attempts — repeats collapse into one row with
                   a count. So this is "accounts seeing failures", which is the
                   more useful number; the attempt total is on the event. */
                <Badge tone="warn">
                  {count(signIns.failedLast24h)} account
                  {signIns.failedLast24h === 1 ? '' : 's'} saw failures
                </Badge>
              ) : null
            }
          />
        ) : null}
        {users ? (
          <Row label="Banned" value={<Num value={users.banned} />} note={`${count(users.closed)} closed`} />
        ) : null}
        {credentials ? (
          <>
            <Row label="OAuth clients" value={<Num value={credentials.oauthClients} />} />
            <Row label="Service credentials" value={<Num value={credentials.serviceCredentials} />} />
          </>
        ) : null}
      </dl>
    </section>
  );
}

function Row({ label, value, note }: { label: string; value: React.ReactNode; note?: React.ReactNode }) {
  return (
    <div style={s.row}>
      <dt style={{ color: C.textMid, fontSize: '.85rem' }}>{label}</dt>
      <dd style={s.rowValue}>
        {note ? <span style={{ fontSize: '.78rem', color: C.muted }}>{note}</span> : null}
        <strong style={{ fontWeight: 660 }}>{value}</strong>
      </dd>
    </div>
  );
}

function RecentActivity({
  events,
  navigate,
}: {
  events: FeedEvent[];
  navigate: (to: string) => void;
}) {
  return (
    <section style={s.panel}>
      <header style={s.panelHead}>
        <h2 style={s.panelTitle}>Recent activity</h2>
        <button type="button" onClick={() => navigate('/activity')} style={s.link}>
          View all
        </button>
      </header>

      {events.length === 0 ? (
        <EmptyState
          title="Nothing recorded yet"
          suggestion="Sign-ins, bans and staff actions appear here as they happen."
        />
      ) : (
        <ul style={s.feed}>
          {events.map((e) => (
            <li key={e.id} style={s.feedItem}>
              <span style={{ ...s.dot, background: dotFor(e.type) }} aria-hidden />
              <span style={{ minWidth: 0 }}>
                <span style={s.feedType}>{e.type}</span>
                <span style={s.feedSubject}>
                  <Identity
                    email={e.subject.email}
                    name={e.subject.name}
                    pseudonymized={e.subject.pseudonymized}
                  />
                </span>
              </span>
              <span style={{ marginLeft: 'auto', color: C.muted, fontSize: '.78rem' }}>
                <When iso={e.createdAt} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The dot colour, derived from the event type rather than stored with it.
 *
 * The old service stored a `dot` hex value on every activity row, which made a
 * rebrand a database migration and let two rows of the same kind disagree.
 * Presentation belongs here; the server sends a machine-readable type.
 */
function dotFor(type: string): string {
  if (type.includes('failed') || type.includes('banned') || type.includes('revoked')) return C.danger;
  if (type.startsWith('user.')) return C.warnDot;
  if (type === 'session.signed-in') return C.successDot;
  return C.mutedDot;
}

function percent(part: number, whole: number): string {
  if (!whole) return '0%';
  const pct = (part / whole) * 100;
  // Below a tenth of a percent, "0.0%" reads as none at all when it is not.
  return pct > 0 && pct < 0.1 ? '<0.1%' : `${pct.toFixed(pct < 10 ? 1 : 0)}%`;
}

const s = {
  kpis: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: '1rem',
  },
  kpi: {
    position: 'relative' as const,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: '.9rem 1.1rem .95rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '.15rem',
    overflow: 'hidden',
  },
  accent: { position: 'absolute' as const, left: 0, top: 0, bottom: 0, width: 3 },
  kpiLabel: { fontSize: '.79rem', color: C.textMid, fontWeight: 600 },
  kpiValue: { fontSize: '1.85rem', fontWeight: 660, lineHeight: 1.15, letterSpacing: '-.02em' },
  kpiTrend: { fontSize: '.79rem' },
  pair: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
    gap: '1rem',
    alignItems: 'start',
  },
  split: {
    display: 'grid',
    // Two thirds / one third, collapsing to one column when there is not room.
    gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)',
    gap: '1rem',
    alignItems: 'start',
  },
  panel: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    overflow: 'hidden',
  },
  panelHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    padding: '.85rem 1.1rem',
    borderBottom: `1px solid ${C.border}`,
  },
  panelTitle: { margin: 0, fontSize: '.92rem', fontWeight: 650 },
  link: {
    background: 'none',
    border: 'none',
    color: C.brand,
    font: 'inherit',
    fontSize: '.82rem',
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
  },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '.88rem' },
  th: {
    textAlign: 'left' as const,
    padding: '.55rem 1.1rem',
    fontSize: '.7rem',
    letterSpacing: '.06em',
    textTransform: 'uppercase' as const,
    color: C.muted,
    fontWeight: 640,
    borderBottom: `1px solid ${C.border}`,
    whiteSpace: 'nowrap' as const,
  },
  thRight: { textAlign: 'right' as const },
  td: { padding: '.6rem 1.1rem', borderBottom: `1px solid ${C.border3}`, verticalAlign: 'top' as const },
  tdRight: { textAlign: 'right' as const },
  rowLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    fontWeight: 600,
    color: C.brand,
    cursor: 'pointer',
    textAlign: 'left' as const,
  },
  sub: {
    fontSize: '.79rem',
    color: C.muted,
    marginTop: 2,
    maxWidth: 380,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  rows: { margin: 0, padding: '.35rem 0' },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '.75rem',
    padding: '.45rem 1.1rem',
  },
  rowValue: { margin: 0, display: 'flex', alignItems: 'center', gap: '.5rem', ...numeric },
  feed: { listStyle: 'none', margin: 0, padding: '.25rem 0' },
  feedItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '.55rem',
    padding: '.42rem 1.1rem',
  },
  dot: { width: 7, height: 7, borderRadius: '50%', flex: 'none' },
  feedType: {
    display: 'block',
    fontFamily: 'ui-monospace, monospace',
    fontSize: '.75rem',
    color: C.textMid,
  },
  feedSubject: {
    display: 'block',
    fontSize: '.79rem',
    color: C.muted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    maxWidth: 210,
  },
};
